/**
 * TaskTemplates store — leaf module (Perf 3.1.b Step 5).
 */
import { create } from 'zustand';
import { taskTemplatesApi } from '@/lib/api/taskTemplates';

const useTaskTemplatesStore = create((set, get) => ({
  // State
  templates: [],
  categories: [],
  isLoading: false,
  error: null,
  selectedCategory: null,
  isFetched: false,

  // Actions
  fetchTemplates: async (options = {}) => {
    try {
      set({ isLoading: true, error: null });
      const { taskTemplatesApi } = await import('@/lib/api');
      const result = await taskTemplatesApi.fetchTemplates(options);
      
      set({
        templates: result.templates || [],
        categories: result.categories || get().categories,
        isLoading: false,
        isFetched: true,
      });
      
      return result;
    } catch (error) {
      set({
        isLoading: false,
        error: error.message || 'Failed to fetch templates',
      });
      throw error;
    }
  },

  fetchCategories: async () => {
    try {
      const { taskTemplatesApi } = await import('@/lib/api');
      const categories = await taskTemplatesApi.fetchCategories();
      set({ categories });
      return categories;
    } catch (error) {
      console.error('Failed to fetch categories:', error);
      throw error;
    }
  },

  setSelectedCategory: (category) => {
    set({ selectedCategory: category });
  },

  // Selectors
  getTemplateBySlug: (slug) => get().templates.find(t => t.slug === slug),

  // Filter by category. The frontend canonicalizes on slug strings (e.g.
  // "faith") but historic data and admin tools occasionally surface the
  // ObjectId form, so we try both. `categories` is consulted to translate
  // an id ↔ slug when needed.
  getTemplatesByCategory: (category) => {
    const templates = get().templates;
    if (!category) return templates;
    const wanted = String(category);
    const cats = get().categories || [];
    const cat = cats.find(
      (c) => c?.slug === wanted || c?.id === wanted || String(c?._id) === wanted,
    );
    const slug = cat?.slug || wanted;
    return templates.filter(
      (t) =>
        t.category === slug ||
        t.category === wanted ||
        String(t.categoryId) === wanted ||
        String(t.categoryId) === String(cat?._id || ''),
    );
  },

  getFeaturedTemplates: () => {
    return get().templates.filter(t => t.flags?.isFeatured);
  },

  // Reset
  clearTemplates: () => {
    set({
      templates: [],
      categories: [],
      isLoading: false,
      error: null,
      selectedCategory: null,
      isFetched: false,
    });
  },
}));

export { useTaskTemplatesStore };
export default useTaskTemplatesStore;
